/**
 * Sign in, sign up, confirm email, forgot and reset password.
 * Visible copy follows the house style: plain words, no hyphens, no emoji.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, type AuthOptions } from "../lib/api";

const ERRORS: Record<string, string> = {
  google_failed: "Google sign in did not work. Please try again.",
  google_unverified: "Google could not confirm this email address.",
  account_disabled: "This account is not active. Ask an admin of your team.",
  invite_only: "Agora is invite only. Ask an admin of your team for an invite.",
  invite_other_email: "This invite is for a different email address than your Google account.",
};

function useOptions(): AuthOptions | null {
  const [opts, setOpts] = useState<AuthOptions | null>(null);
  useEffect(() => {
    api<AuthOptions>("/api/auth/options").then(setOpts, () => setOpts({ google: false, firstRun: false }));
  }, []);
  return opts;
}

function Card({ title, lead, children }: { title: string; lead?: string; children: ReactNode }) {
  return (
    <main className="auth">
      <section className="card" aria-labelledby="auth-title">
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          Agora
        </div>
        <h1 id="auth-title">{title}</h1>
        {lead && <p className="lead">{lead}</p>}
        {children}
      </section>
    </main>
  );
}

function Field(p: { label: string; name: string; type?: string; autoComplete?: string; hint?: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <label htmlFor={p.name}>{p.label}</label>
      <input
        id={p.name}
        name={p.name}
        type={p.type ?? "text"}
        autoComplete={p.autoComplete}
        required
        value={p.value}
        onChange={(e) => p.onChange(e.target.value)}
      />
      {p.hint && <small>{p.hint}</small>}
    </div>
  );
}

function GoogleButton({ label }: { label: string }) {
  return (
    <a className="btn ghost" href="/api/auth/google">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      {label}
    </a>
  );
}

function Notice({ kind, children }: { kind: "error" | "ok" | "info"; children: ReactNode }) {
  return (
    <div className={`notice ${kind}`} role={kind === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}

export function SignIn() {
  const opts = useOptions();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(ERRORS[params.get("error") ?? ""] ?? null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setUnverified(false);
    try {
      await api("/api/auth/login", { email, password });
      const invite = params.get("invite");
      if (invite) await api("/api/invites/accept", { token: invite });
      navigate("/", { replace: true });
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setError(apiErr?.message ?? "Something went wrong. Please try again.");
      setUnverified(apiErr?.code === "email_not_verified");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    await api("/api/auth/resend-verification", { email }).catch(() => undefined);
    setResent(true);
  }

  return (
    <Card title="Sign in" lead="Welcome back to your team's rooms.">
      {error && <Notice kind="error">{error}</Notice>}
      {unverified && !resent && (
        <button className="btn ghost" type="button" onClick={resend} style={{ marginBottom: 14 }}>
          Send the confirmation link again
        </button>
      )}
      {resent && <Notice kind="ok">If that address has an account waiting for confirmation, a new link is on its way.</Notice>}
      {opts?.google && (
        <>
          <GoogleButton label="Continue with Google" />
          <div className="or">or with your email</div>
        </>
      )}
      <form onSubmit={submit} noValidate={false}>
        <Field label="Email" name="email" type="email" autoComplete="email" value={email} onChange={setEmail} />
        <Field label="Password" name="password" type="password" autoComplete="current-password" value={password} onChange={setPassword} />
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Signing in" : "Sign in"}
        </button>
      </form>
      <div className="links">
        <Link to="/forgot-password">Forgot your password?</Link>
        <Link to="/sign-up">Create an account</Link>
      </div>
    </Card>
  );
}

export function SignUp() {
  const opts = useOptions();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/signup", { name, email, password });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <Card title="Check your email" lead={`We sent a link to ${email}. Open it to confirm your address and finish creating your account.`}>
        <Notice kind="info">The link works once and expires in 24 hours. Nothing there? Look in your spam folder.</Notice>
        <Link className="btn ghost" to="/sign-in">
          Back to sign in
        </Link>
      </Card>
    );
  }

  return (
    <Card title="Create your account" lead="Agora is where your team and its agents talk.">
      {opts?.firstRun && <Notice kind="info">You are the first person here, so your account becomes the Admin of this workspace.</Notice>}
      {error && <Notice kind="error">{error}</Notice>}
      {opts?.google && (
        <>
          <GoogleButton label="Sign up with Google" />
          <div className="or">or with your email</div>
        </>
      )}
      <form onSubmit={submit}>
        <Field label="Name" name="name" autoComplete="name" value={name} onChange={setName} />
        <Field label="Email" name="email" type="email" autoComplete="email" value={email} onChange={setEmail} />
        <Field label="Password" name="password" type="password" autoComplete="new-password" hint="At least 12 characters. A short sentence works well." value={password} onChange={setPassword} />
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Creating your account" : "Create account"}
        </button>
      </form>
      <div className="links">
        <span>
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </span>
      </div>
    </Card>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // the link works once; do not post it twice in development
    started.current = true;
    api("/api/auth/verify-email", { token: params.get("token") ?? "" }).then(
      () => navigate("/", { replace: true }),
      (err) => setError(err instanceof ApiError ? err.message : "This link is not valid."),
    );
  }, [params, navigate]);

  return (
    <Card title={error ? "This link did not work" : "Confirming your email"}>
      {error ? (
        <>
          <Notice kind="error">{error}</Notice>
          <Link className="btn ghost" to="/sign-in">
            Go to sign in
          </Link>
        </>
      ) : (
        <p className="lead">One moment.</p>
      )}
    </Card>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Reset your password" lead="Enter your email and we will send you a link to choose a new password.">
      {sent ? (
        <Notice kind="ok">If {email} has an Agora account, the link is on its way. It expires in 30 minutes.</Notice>
      ) : (
        <form onSubmit={submit}>
          {error && <Notice kind="error">{error}</Notice>}
          <Field label="Email" name="email" type="email" autoComplete="email" value={email} onChange={setEmail} />
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? "Sending" : "Send the link"}
          </button>
        </form>
      )}
      <div className="links">
        <Link to="/sign-in">Back to sign in</Link>
      </div>
    </Card>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/reset-password", { token, password });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        // A refused password uses up the link; the server hands back a fresh one.
        if (typeof err.data?.token === "string") setToken(err.data.token);
      } else setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Choose a new password" lead="You will be signed out of every other device.">
      {error && <Notice kind="error">{error}</Notice>}
      <form onSubmit={submit}>
        <Field label="New password" name="password" type="password" autoComplete="new-password" hint="At least 12 characters. A short sentence works well." value={password} onChange={setPassword} />
        <button className="btn primary" type="submit" disabled={busy || !token}>
          {busy ? "Saving" : "Save and sign in"}
        </button>
      </form>
      <div className="links">
        <Link to="/forgot-password">Ask for a new link</Link>
      </div>
    </Card>
  );
}
